<template>
  <div class="markdown-editor-host">
    <div class="milkdown-shell crepe-shell" :data-read-only="canEdit ? 'false' : 'true'">
      <div ref="editorRoot" class="milkdown-editor-root crepe-editor-root"></div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Crepe } from '@milkdown/crepe';
import { parserCtx } from '@milkdown/core';
import '@milkdown/crepe/theme/common/style.css';
import '@milkdown/crepe/theme/frame.css';
import { replaceAll } from '@milkdown/utils';
import { Selection, TextSelection } from 'prosemirror-state';
import type { EditorView } from 'prosemirror-view';
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import type { EntityId } from '../api/entityId';
import { createReadableDiffPlugin, hideReadableDiff, showReadableDiff } from '../editor/readableDiff';
import { createHeadingFoldingPlugin, setHeadingFoldingSuppressed } from '../editor/headingFolding';
import { getChangedMarkdown, sanitizeMarkdown } from '../editor/markdown';
import type { EditorViewportPosition } from '../stores/workspaceViewport';

const props = defineProps<{
  documentId: EntityId;
  paneIndex: number;
  contentMarkdown: string;
  canEdit: boolean;
  initialViewportPosition: EditorViewportPosition | null;
  initialCollapsedHeadingPositions: readonly number[];
  readableDiffBaselineMarkdown: string | null;
}>();

const emit = defineEmits<{
  change: [contentMarkdown: string];
  viewportChange: [documentId: EntityId, position: EditorViewportPosition];
  collapsedHeadingPositionsChange: [documentId: EntityId, positions: number[]];
  readableDiffCleared: [];
}>();

const editorRoot = ref<HTMLElement | null>(null);
let crepe: Crepe | null = null;
let renderedDocumentId: EntityId | null = null;
let lastMarkdown = '';
let suppressChange = false;
let suppressViewportChange = false;
let editorScrollElement: HTMLElement | null = null;
let viewportChangeTimer: number | null = null;
let restoreFrame: number | null = null;

onMounted(async () => {
  await createEditor();
});

onBeforeUnmount(async () => {
  await destroyEditor();
});

watch(
  () => props.documentId,
  async () => {
    await createEditor();
  }
);

watch(
  () => props.readableDiffBaselineMarkdown,
  () => {
    updateReadableDiff();
  }
);

watch(
  () => props.contentMarkdown,
  async content => {
    if (crepe && renderedDocumentId === props.documentId) {
      await replaceEditorContent(content);
    }
  }
);

watch(
  () => props.canEdit,
  canEdit => {
    if (crepe) {
      crepe.setReadonly(!canEdit);
    }
  }
);

async function createEditor(): Promise<void> {
  await nextTick();
  if (!editorRoot.value) {
    return;
  }

  await destroyEditor();
  const documentId = props.documentId;
  const content = sanitizeMarkdown(props.contentMarkdown);
  lastMarkdown = content;
  suppressChange = true;
  crepe = new Crepe({
    root: editorRoot.value,
    defaultValue: content,
    features: {
      [Crepe.Feature.TopBar]: true,
      [Crepe.Feature.BlockEdit]: false,
      [Crepe.Feature.Toolbar]: false
    }
  });
  crepe.editor.use(
    createHeadingFoldingPlugin(props.initialCollapsedHeadingPositions, positions => {
      emit('collapsedHeadingPositionsChange', documentId, positions);
    })
  );
  crepe.editor.use(
    createReadableDiffPlugin(() => {
      const view = getEditorView();
      if (view) {
        setHeadingFoldingSuppressed(view, false);
      }

      emit('readableDiffCleared');
    })
  );

  crepe.on(listener => {
    listener.markdownUpdated((_, markdown, previousMarkdown) => {
      const sanitizedMarkdown = sanitizeMarkdown(markdown);
      const changedMarkdown = getChangedMarkdown(markdown, previousMarkdown);
      lastMarkdown = sanitizedMarkdown;
      if (!suppressChange && props.canEdit && changedMarkdown != null) {
        emit('change', changedMarkdown);
      }
    });
    listener.selectionUpdated(() => {
      scheduleViewportChange();
    });
    listener.blur(() => {
      flushViewportChange();
    });
  });

  await crepe.create();
  crepe.setReadonly(!props.canEdit);
  renderedDocumentId = props.documentId;
  lastMarkdown = getEditorMarkdown();
  registerEditorScrollListener();
  restoreViewportPosition();
  updateReadableDiff();
  suppressChange = false;
}

async function destroyEditor(): Promise<void> {
  flushViewportChange();
  cancelViewportRestore();
  unregisterEditorScrollListener();

  if (crepe) {
    await crepe.destroy();
    crepe = null;
  }

  if (editorRoot.value) {
    editorRoot.value.replaceChildren();
  }

  renderedDocumentId = null;
}

async function replaceEditorContent(content: string): Promise<void> {
  if (!crepe) {
    return;
  }

  const markdown = sanitizeMarkdown(content);
  if (lastMarkdown === markdown || getEditorMarkdown() === markdown) {
    lastMarkdown = markdown;
    return;
  }

  suppressChange = true;
  suppressViewportChange = true;
  try {
    crepe.editor.action(replaceAll(markdown, true));
    lastMarkdown = markdown;
  } finally {
    suppressChange = false;
    suppressViewportChange = false;
  }
}

function getEditorMarkdown(): string {
  if (!crepe) {
    return lastMarkdown;
  }

  return sanitizeMarkdown(crepe.getMarkdown());
}

function registerEditorScrollListener(): void {
  editorScrollElement = getEditorScrollElement();
  editorScrollElement?.addEventListener('scroll', scheduleViewportChange, { passive: true });
}

function unregisterEditorScrollListener(): void {
  if (editorScrollElement) {
    editorScrollElement.removeEventListener('scroll', scheduleViewportChange);
    editorScrollElement = null;
  }
}

function scheduleViewportChange(): void {
  if (suppressViewportChange) {
    return;
  }

  if (viewportChangeTimer != null) {
    window.clearTimeout(viewportChangeTimer);
  }

  viewportChangeTimer = window.setTimeout(() => {
    viewportChangeTimer = null;
    emitViewportChange();
  }, 200);
}

function flushViewportChange(): void {
  if (viewportChangeTimer != null) {
    window.clearTimeout(viewportChangeTimer);
    viewportChangeTimer = null;
  }

  emitViewportChange();
}

function emitViewportChange(): void {
  if (suppressViewportChange || renderedDocumentId == null) {
    return;
  }

  const position = getViewportPosition();
  if (position == null) {
    return;
  }

  emit('viewportChange', renderedDocumentId, position);
}

function getViewportPosition(): EditorViewportPosition | null {
  const view = getEditorView();
  const scrollElement = getEditorScrollElement();
  if (view == null || scrollElement == null) {
    return null;
  }

  return {
    scrollTop: scrollElement.scrollTop,
    selectionAnchor: view.state.selection.anchor,
    selectionHead: view.state.selection.head
  };
}

function restoreViewportPosition(): void {
  const position = props.initialViewportPosition;
  if (position == null) {
    return;
  }

  suppressViewportChange = true;
  restoreSelection(position);
  restoreFrame = window.requestAnimationFrame(() => {
    const scrollElement = getEditorScrollElement();
    if (scrollElement) {
      scrollElement.scrollTop = position.scrollTop;
    }

    suppressViewportChange = false;
    restoreFrame = null;
  });
}

function restoreSelection(position: EditorViewportPosition): void {
  const view = getEditorView();
  if (view == null) {
    return;
  }

  const maxPosition = view.state.doc.content.size;
  const anchor = clampSelectionPosition(position.selectionAnchor, maxPosition);
  const head = clampSelectionPosition(position.selectionHead, maxPosition);
  const selection = createSelection(view, anchor, head);
  view.dispatch(view.state.tr.setSelection(selection));
}

function createSelection(view: EditorView, anchor: number, head: number): Selection {
  try {
    return TextSelection.create(view.state.doc, anchor, head);
  } catch {
    return Selection.near(view.state.doc.resolve(anchor), anchor <= head ? 1 : -1);
  }
}

function clampSelectionPosition(position: number, maxPosition: number): number {
  return Math.max(0, Math.min(position, maxPosition));
}

function cancelViewportRestore(): void {
  if (restoreFrame != null) {
    window.cancelAnimationFrame(restoreFrame);
    restoreFrame = null;
  }

  suppressViewportChange = false;
}

function getEditorScrollElement(): HTMLElement | null {
  return editorRoot.value?.querySelector<HTMLElement>('.ProseMirror') ?? null;
}

function getEditorView(): EditorView | null {
  if (!crepe) {
    return null;
  }

  return crepe.editor.action(ctx => ctx.get<EditorView, 'editorView'>('editorView'));
}

function updateReadableDiff(): void {
  const view = getEditorView();
  if (view == null || crepe == null) {
    return;
  }

  const baselineMarkdown = props.readableDiffBaselineMarkdown;
  if (baselineMarkdown == null) {
    hideReadableDiff(view);
    setHeadingFoldingSuppressed(view, false);
    return;
  }

  const baselineDoc = crepe.editor.action(ctx => ctx.get(parserCtx)(sanitizeMarkdown(baselineMarkdown)));
  showReadableDiff(view, baselineDoc);
  setHeadingFoldingSuppressed(view, true);
}
</script>
